// Simplified unit conversion system for cosmetics/beauty industry
// Only includes units actually used by beauty companies

// Base conversion factors
export const UNIT_CONVERSIONS = {
  // Weight conversions to grams (base unit)
  'g': 1,
  'kg': 1000,
  'lbs': 453.592,
  'oz': 28.3495, // Weight ounces (NOT fluid ounces)
  
  // Volume conversions to milliliters (base unit)
  'ml': 1,
  'L': 1000,
  'gal': 3785.41, // US gallon
};

// Define unit categories - simplified
export const WEIGHT_UNITS = ['g', 'kg', 'lbs', 'oz'];
export const VOLUME_UNITS = ['ml', 'L', 'gal'];

// Unit type checking functions
export const isWeightUnit = (unit) => WEIGHT_UNITS.includes(unit);
export const isVolumeUnit = (unit) => VOLUME_UNITS.includes(unit);

// Get unit type
export const getUnitType = (unit) => {
  if (isWeightUnit(unit)) return 'weight';
  if (isVolumeUnit(unit)) return 'volume';
  return 'unknown';
};

// Convert between units of the same type
export const convertSameType = (value, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return value;
  
  const unitType = getUnitType(fromUnit);
  if (unitType !== getUnitType(toUnit)) {
    throw new Error(`Cannot convert between ${fromUnit} and ${toUnit} - different unit types`);
  }
  
  if (unitType === 'weight') {
    // Convert weight units via grams
    const grams = value * UNIT_CONVERSIONS[fromUnit];
    return grams / UNIT_CONVERSIONS[toUnit];
  } else if (unitType === 'volume') {
    // Convert volume units via milliliters
    const milliliters = value * UNIT_CONVERSIONS[fromUnit];
    return milliliters / UNIT_CONVERSIONS[toUnit];
  }
  
  throw new Error(`Unsupported unit type: ${unitType}`);
};

// Convert between different unit types using density
export const convertWithDensity = (value, fromUnit, toUnit, density = null) => {
  if (fromUnit === toUnit) return value;
  
  const fromType = getUnitType(fromUnit);
  const toType = getUnitType(toUnit);
  
  if (fromType === toType) {
    return convertSameType(value, fromUnit, toUnit);
  }
  
  // Cross-type conversion requires density
  if (!density || density <= 0) {
    throw new Error(`Density required for conversion between ${fromUnit} and ${toUnit}`);
  }
  
  if (fromType === 'weight' && toType === 'volume') {
    // Weight to volume: mass(g) / density(g/ml) = volume(ml)
    const grams = convertSameType(value, fromUnit, 'g');
    const milliliters = grams / density;
    return convertSameType(milliliters, 'ml', toUnit);
  } else if (fromType === 'volume' && toType === 'weight') {
    // Volume to weight: volume(ml) * density(g/ml) = mass(g)
    const milliliters = convertSameType(value, fromUnit, 'ml');
    const grams = milliliters * density;
    return convertSameType(grams, 'g', toUnit);
  }
  
  throw new Error(`Cannot convert from ${fromType} to ${toType}`);
};

// Main conversion function with automatic type detection
export const convertUnits = (value, fromUnit, toUnit, density = null) => {
  if (!value || !fromUnit || !toUnit) return value;
  if (fromUnit === toUnit) return value;
  
  try {
    const fromType = getUnitType(fromUnit);
    const toType = getUnitType(toUnit);
    
    if (fromType === 'unknown' || toType === 'unknown') {
      console.warn(`Unknown unit type: ${fromUnit} -> ${toUnit}`);
      return value;
    }
    
    if (fromType === toType) {
      return convertSameType(value, fromUnit, toUnit);
    } else {
      return convertWithDensity(value, fromUnit, toUnit, density);
    }
  } catch (error) {
    console.warn(`Unit conversion failed: ${error.message}`);
    return value;
  }
};

// Calculate raw material usage in target units
export const calculateRawMaterialUsage = (
  formulationAmount,
  formulationUnit,
  rawMaterialConfig,
  targetUnit = null
) => {
  if (!rawMaterialConfig || !formulationAmount) {
    return {
      amount: 0,
      unit: targetUnit || formulationUnit,
      unitsNeeded: 0,
      unitCost: 0
    };
  }
  
  const materialUnit = rawMaterialConfig.primary_unit_measure || 'g';
  const displayUnit = targetUnit || materialUnit;
  
  try {
    // Convert formulation amount to material units
    const amountInMaterialUnits = convertUnits(
      formulationAmount,
      formulationUnit,
      materialUnit,
      rawMaterialConfig.density_g_ml
    );
    
    // Convert to display units if different
    const displayAmount = convertUnits(
      amountInMaterialUnits,
      materialUnit,
      displayUnit,
      rawMaterialConfig.density_g_ml
    );
    
    // Calculate purchase units needed
    const primaryUnitQuantity = rawMaterialConfig.primary_unit_quantity || 1;
    const unitsNeeded = Math.ceil(amountInMaterialUnits / primaryUnitQuantity);
    
    // Calculate unit cost
    const unitCost = rawMaterialConfig.unit_cost || 0;
    
    return {
      amount: displayAmount,
      unit: displayUnit,
      unitsNeeded,
      unitCost: unitCost
    };
  } catch (error) {
    console.warn(`Error calculating raw material usage: ${error.message}`);
    return {
      amount: formulationAmount,
      unit: displayUnit,
      unitsNeeded: 0,
      unitCost: 0
    };
  }
};

// Calculate raw material cost
export const calculateRawMaterialCost = (
  formulationAmount,
  formulationUnit,
  rawMaterial
) => {
  if (!rawMaterial || !formulationAmount) {
    return {
      totalCost: 0,
      unitCost: 0,
      unitsNeeded: 0,
      costPerFormulationUnit: 0
    };
  }
  
  const materialUnit = rawMaterial.primary_unit_measure || 'g';
  const unitCost = rawMaterial.unit_cost || 0;
  const primaryUnitQuantity = rawMaterial.primary_unit_quantity || 1;
  
  try {
    // Convert formulation amount to material units
    const amountInMaterialUnits = convertUnits(
      formulationAmount,
      formulationUnit,
      materialUnit,
      rawMaterial.density_g_ml
    );
    
    // Calculate cost directly from amount in material units since unit_cost is now per unit measure (kg, ml, etc.)
    const totalCost = amountInMaterialUnits * unitCost;
    
    // Calculate purchase units needed (rounded up for purchasing) - still useful for container purchasing
    const exactUnitsNeeded = amountInMaterialUnits / primaryUnitQuantity;
    const unitsNeeded = Math.ceil(exactUnitsNeeded);
    
    // Calculate cost per formulation unit
    const costPerFormulationUnit = formulationAmount > 0 ? totalCost / formulationAmount : 0;
    
    return {
      totalCost,
      unitCost,
      unitsNeeded,
      costPerFormulationUnit
    };
  } catch (error) {
    console.warn(`Error calculating raw material cost: ${error.message}`);
    return {
      totalCost: 0,
      unitCost: 0,
      unitsNeeded: 0,
      costPerFormulationUnit: 0
    };
  }
};

// Format unit display
export const formatUnitDisplay = (value, unit, precision = 2) => {
  if (value === null || value === undefined) return '—';
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '—';
  
  return `${numValue.toFixed(precision)} ${unit}`;
};

// Format raw material unit information
export const formatRawMaterialUnit = (rawMaterial) => {
  if (!rawMaterial) return 'Unknown unit';
  
  const quantity = rawMaterial.primary_unit_quantity || 1;
  const unit = rawMaterial.primary_unit_measure || 'g';
  
  if (quantity === 1) {
    return unit;
  }
  
  return `${quantity}${unit}`;
};

// Check if units are compatible for conversion
export const areUnitsCompatible = (fromUnit, toUnit, hasDensity = false) => {
  const fromType = getUnitType(fromUnit);
  const toType = getUnitType(toUnit);
  
  if (fromType === 'unknown' || toType === 'unknown') return false;
  if (fromType === toType) return true;
  
  // Cross-type conversion requires density
  return hasDensity;
};

// Get suggested units for a given type
export const getSuggestedUnits = (unitType) => {
  switch (unitType) {
    case 'weight':
      return WEIGHT_UNITS;
    case 'volume':
      return VOLUME_UNITS;
    default:
      return [...WEIGHT_UNITS, ...VOLUME_UNITS];
  }
};

// Validate unit
export const isValidUnit = (unit) => {
  return WEIGHT_UNITS.includes(unit) || VOLUME_UNITS.includes(unit);
};

// Get all available units
export const getAllUnits = () => [...WEIGHT_UNITS, ...VOLUME_UNITS];

// Get unit symbol for display
export const getUnitSymbol = (unit) => {
  const symbols = {
    'g': 'g',
    'kg': 'kg', 
    'lbs': 'lbs',
    'oz': 'oz', // Weight ounces
    'ml': 'ml',
    'L': 'L',
    'gal': 'gal'
  };
  return symbols[unit] || unit;
};

export default {
  UNIT_CONVERSIONS,
  WEIGHT_UNITS,
  VOLUME_UNITS,
  isWeightUnit,
  isVolumeUnit,
  getUnitType,
  convertSameType,
  convertWithDensity,
  convertUnits,
  calculateRawMaterialUsage,
  calculateRawMaterialCost,
  formatUnitDisplay,
  formatRawMaterialUnit,
  areUnitsCompatible,
  getSuggestedUnits,
  isValidUnit,
  getAllUnits,
  getUnitSymbol
}; 